/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { SparingRequestConfiguration } from 'models/SparingRequestConfig';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_CALVIN_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_CALVIN;

export const calvinBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const divisionId = (api.getState() as ReduxState).app.acuityContext?.selectedCustomer.id;
  const isDiagnostic = typeof args !== 'string' && (args.url === '/diagnostics/version' || args.url === '/diagnostics/apiName');

  if (!divisionId && !isDiagnostic) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No division ID received'
      }
    };
  }
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = isDiagnostic ? args.url : `divisions/${divisionId}/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);

        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const sparingRequestConfig = createApi({
  reducerPath: 'sparingRequestConfigApi',
  baseQuery: calvinBaseQuery,
  tagTypes: ['Configs', 'Config'],
  endpoints: (builder) => ({
    getSparingRequestConfiguration: builder.query<SparingRequestConfiguration, void>({
      query: () => ({
        url: `/sparingRequestConfiguration`
      }),
      providesTags: ['Config']
    }),

    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getInfo: builder.query<{ version: string; apiName: string }, void>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ): Promise<any> {
        const version = await fetchWithBQ({ url: '/diagnostics/version', responseHandler: (response): Promise<string> => response.text() });
        const apiName = await fetchWithBQ({ url: '/diagnostics/apiName', responseHandler: (response): Promise<string> => response.text() });

        return { data: { version: version.data, apiName: apiName.data } };
      }
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetSparingRequestConfigurationQuery, useGetApiNameQuery, useGetVersionQuery, useGetInfoQuery } = sparingRequestConfig;
